// src/App.tsx
import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  redirect,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createTheme } from "@mui/material/styles";

import {
  SupabaseProvider,
  useSupabase,
  supabase,
} from "./providers/SupabaseProvider";
import { GameStateProvider } from "./providers/GameStateProvider";

// pages
import Home from "./pages/Home";
import Budgeting from "./pages/Budgeting";
import History from "./pages/History";
import Settings from "./pages/Settings";
import ResetPassword from "./pages/ResetPassword";
import Showcase from "./pages/Showcase";

/* ------------------------------------------------------------------ */
/*  MUI theme                                                          */
/* ------------------------------------------------------------------ */
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2196f3" },
    secondary: { main: "#f50057" },
  },
});

/* ------------------------------------------------------------------ */
/*  Auth-guarded layout                                               */
/* ------------------------------------------------------------------ */
const ProtectedLayout = () => {
  const { session } = useSupabase();

  if (!session) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

/* ------------------------------------------------------------------ */
/*  Router                                                             */
/* ------------------------------------------------------------------ */
const router = createBrowserRouter([
  {
    path: "/",
    element: <Home />,
    loader: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        return redirect("/budget");
      }
      return null;
    },
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    loader: async () => {
      // Supabase automatically sets the session when the page is loaded
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        return redirect("/");
      }
      return null;
    },
  },
  {
    element: <ProtectedLayout />,
    children: [
      {
        path: "/budget",
        element: <Budgeting />,
      },
      {
        path: "/history",
        element: <History />,
      },
      {
        path: "/settings",
        element: <Settings />,
      },
      {
        path: "/showcase",
        element: <Showcase />,
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

/* ------------------------------------------------------------------ */
/*  App                                                                */
/* ------------------------------------------------------------------ */
const App: React.FC = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <SupabaseProvider>
      <GameStateProvider>
        <RouterProvider router={router} />
      </GameStateProvider>
    </SupabaseProvider>
  </ThemeProvider>
);

export default App;
